import React, { useEffect, useState, useCallback } from "react";
import { connect, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { Button, CircularProgress } from "@mui/material";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import moment from "moment";
import LayoutTopSideBottom from "../../layouts/LayoutTopSideBottom/LayoutTopSideBottom";
import AIDEConsoleUserTable from "./AIDEConsoleUserTable";
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import Loader from "../../components/Loader/Loader";
import noDataImage from "../../../assets/images/undraw_empty.svg";
import { config } from "../../config/config";
import {
  aideConsoleUpdateUsers,
  aideConsoleUpdateCountryBrands,
  aideConsoleUpdateSearchUsers,
  aideConsoleCleanSearchUsers,
  aideConsoleUpdateUserList,
  aideConsoleReset,
  updateAlertInfo,
} from "../../redux/actions";
import "./AIDEConsole.scss";

/**
 * Normalize a user record from the Data Reckitt Firestore DB
 * into the format expected by our table component.
 *
 * Data Reckitt document has:
 *   { user_id, name (encrypted), role, active_status, created_on, oid, user_type, security_filters }
 *
 * Our table expects:
 *   { user_id, name, role, access_granted_by, access_granted_on, security_filters }
 */
function normalizeUser(item) {
  try {
    const sf = item.security_filters;
    // Ensure security_filters is always a non-empty array
    const security_filters =
      Array.isArray(sf) && sf.length > 0
        ? sf
        : [{ country: "All", brands_list: ["All"] }];

    // Use user_id as display name if name looks encrypted (base64) or is empty
    const name = item.name || item.user_id || "";

    return {
      user_id: item.user_id || "",
      name: name,
      role: item.role || "user",
      active_status: item.active_status || "",
      access_granted_by: item.access_granted_by || "",
      access_granted_on: item.access_granted_on || 0,
      security_filters: security_filters,
      oid: item.oid || "",
      user_type: item.user_type || "",
    };
  } catch (e) {
    console.error("Error normalizing user:", item, e);
    return {
      user_id: item?.user_id || "unknown",
      name: item?.user_id || "unknown",
      role: "user",
      active_status: "",
      access_granted_by: "",
      access_granted_on: 0,
      security_filters: [{ country: "All", brands_list: ["All"] }],
      oid: "",
      user_type: "",
    };
  }
}

function AIDEConsole(props) {
  const { user, aideConsole, match } = props;
  const dispatch = useDispatch();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [hasChanges, setHasChanges] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState("user");

  const theme = user?.ui_preferences?.theme || "light";

  // Fetch user role + users on mount
  useEffect(() => {
    fetchUserRole();
    fetchUsers();
    fetchCountryBrands();
    return () => {
      dispatch(aideConsoleReset());
    };
  }, []);

  const fetchUserRole = useCallback(() => {
    fetch(config.api.aideConsoleUserRoleUrl, { credentials: "include" })
      .then((res) => res.json())
      .then((json) => {
        if (json.data && json.data.role) {
          setCurrentUserRole(json.data.role);
        }
      })
      .catch((err) => {
        console.error("Error fetching user role:", err);
      });
  }, []);

  const fetchUsers = useCallback(() => {
    setLoading(true);
    fetch(config.api.aideConsoleUsersUrl, { credentials: "include" })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}: ${res.statusText}`);
        }
        return res.json();
      })
      .then((json) => {
        console.log("AIDE Console: Received", json.data?.length || 0, "users");
        if (json.data && Array.isArray(json.data)) {
          const normalized = json.data.map(normalizeUser);
          dispatch(aideConsoleUpdateUsers(normalized));
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching AIDE console users:", err);
        setLoading(false);
        dispatch(
          updateAlertInfo({
            open: true,
            message: `Failed to load users: ${err.message}`,
            severity: "error",
          }),
        );
      });
  }, [dispatch]);

  const fetchCountryBrands = useCallback(() => {
    fetch(config.api.aideConsoleCountryBrandsUrl, { credentials: "include" })
      .then((res) => res.json())
      .then((json) => {
        if (json.data) {
          dispatch(aideConsoleUpdateCountryBrands(json.data));
        }
      })
      .catch((err) => {
        console.error("Error fetching country brands:", err);
      });
  }, [dispatch]);

  // Search for global users (add user autocomplete)
  const handleSearchUsers = useCallback(
    (term) => {
      if (!term || term.length < 2) {
        dispatch(aideConsoleCleanSearchUsers());
        return;
      }

      fetch(config.api.aideConsoleGlobalUsersUrl, {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ search_text: term }),
      })
        .then((res) => res.json())
        .then((json) => {
          if (json.data) {
            dispatch(aideConsoleUpdateSearchUsers(json.data));
          }
        })
        .catch((err) => {
          console.error("Error searching global users:", err);
        });
    },
    [dispatch],
  );

  // Add a user
  const handleAddUser = (val) => {
    if (!val) return;

    let userEmail = "";
    let userName = "";

    if (typeof val === "string") {
      userEmail = val;
      userName = val;
    } else {
      userEmail = val.user_id || val.email || val.preferred_username || "";
      userName = val.user_name || val.name || userEmail;
    }

    if (!userEmail || typeof userEmail !== "string") return;

    // Check if user already exists
    const currentUsers = aideConsole.users.data || [];
    const exists = currentUsers.find(
      (u) => u.user_id && u.user_id.toLowerCase() === userEmail.toLowerCase(),
    );

    if (exists) {
      dispatch(
        updateAlertInfo({
          open: true,
          message: "User already exists in the list",
          severity: "warning",
        }),
      );
      return;
    }

    const newUser = {
      access_granted_by: user?.preferred_username || "system",
      access_granted_on: moment().valueOf(),
      name: userName,
      role: "user",
      user_id: userEmail,
      security_filters: [
        {
          country: "All",
          brands_list: ["All"],
        },
      ],
    };

    const updatedList = [newUser, ...currentUsers];
    dispatch(aideConsoleUpdateUserList(updatedList));
    dispatch(aideConsoleCleanSearchUsers());
    setSearchTerm("");
    setHasChanges(true);
  };

  // Update user role
  const handleUpdateRole = (row, newRole, sortedList) => {
    if (sortedList) {
      // This is a sorted list update (from table sort)
      dispatch(aideConsoleUpdateUserList(sortedList));
      return;
    }
    if (!row || !newRole) return;

    const currentUsers = aideConsole.users.data || [];
    const updatedList = currentUsers.map((u) =>
      u.user_id === row.user_id ? { ...u, role: newRole } : u,
    );
    dispatch(aideConsoleUpdateUserList(updatedList));
    setHasChanges(true);
  };

  // Delete user
  const handleDeleteUser = (row) => {
    const currentUsers = aideConsole.users.data || [];
    const updatedList = currentUsers.filter(
      (u) => u.user_id?.toLowerCase() !== row?.user_id?.toLowerCase(),
    );
    dispatch(aideConsoleUpdateUserList(updatedList));
    setHasChanges(true);
  };

  // Update security filters
  const handleUpdateSecurityFilters = (row, filters) => {
    const currentUsers = aideConsole.users.data || [];
    const updatedList = currentUsers.map((u) =>
      u.user_id === row.user_id ? { ...u, security_filters: filters } : u,
    );
    dispatch(aideConsoleUpdateUserList(updatedList));
    setHasChanges(true);
  };

  // Save changes
  const handleSaveChanges = () => {
    setSaving(true);
    const usersList = aideConsole.users.data || [];

    fetch(config.api.aideConsoleSaveUsersUrl, {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ users_list: usersList }),
    })
      .then((res) => res.json())
      .then((json) => {
        setSaving(false);
        if (
          json.status &&
          json.status.statusCode &&
          json.status.statusCode.includes("200")
        ) {
          dispatch(
            updateAlertInfo({
              open: true,
              message: "Changes saved successfully",
              severity: "success",
            }),
          );
          setHasChanges(false);
          // Refresh users from server
          fetchUsers();
        } else {
          dispatch(
            updateAlertInfo({
              open: true,
              message: json.status?.statusMessage || "Failed to save changes",
              severity: "error",
            }),
          );
        }
      })
      .catch((err) => {
        setSaving(false);
        console.error("Error saving users:", err);
        dispatch(
          updateAlertInfo({
            open: true,
            message: "Failed to save changes",
            severity: "error",
          }),
        );
      });
  };

  const searchUserOptions = aideConsole.searchUsers.data || [];

  return (
    <LayoutTopSideBottom match={match}>
      <Breadcrumbs match={match} />
      <div className={`${user.theme} app-page aide-console-container`}>
        {user?.role !== config.hardCoded.adminRole ? (
          <>
            <div className="no-apps">
              <img src={noDataImage} />
              <p className="no-data">
                <b>You Don't have access to open AIDE-MROI portal</b>
              </p>
            </div>
          </>
        ) : (
          <>
            {/* Header */}
            <div className="aide-console-header">
              <h2>Access Management</h2>
              <div className="aide-console-actions">
                <Button
                  variant="contained"
                  className="aide-console-save-btn"
                  disabled={!hasChanges || saving}
                  onClick={handleSaveChanges}
                >
                  {saving ? (
                    <CircularProgress size={18} style={{ color: "#fff" }} />
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              </div>
            </div>

            {/* Add User Section */}
            <div className="aide-console-add-user-section">
              <Autocomplete
                options={searchUserOptions}
                filterOptions={(x) => x}
                getOptionLabel={(option) =>
                  typeof option === "string"
                    ? option
                    : option.user_name || option.name || option.user_id || ""
                }
                inputValue={searchTerm}
                onInputChange={(e, val, reason) => {
                  if (reason === "input") {
                    setSearchTerm(val);
                    handleSearchUsers(val);
                  } else if (reason === "clear") {
                    setSearchTerm("");
                    dispatch(aideConsoleCleanSearchUsers());
                  }
                }}
                onChange={(e, val) => {
                  if (val) {
                    handleAddUser(val);
                  }
                }}
                style={{ width: 400 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    size="small"
                    placeholder="Search and add users..."
                  />
                )}
              />
            </div>

            {/* User Table */}
            {loading ? (
              <div className="aide-console-loading">
                <CircularProgress />
              </div>
            ) : (
              <AIDEConsoleUserTable
                accessList={aideConsole.users.data}
                accessListOriginal={aideConsole.users.originalData}
                countryBrandsData={aideConsole.countryBrands.data}
                onUpdateRole={handleUpdateRole}
                onDeleteUser={handleDeleteUser}
                onUpdateSecurityFilters={handleUpdateSecurityFilters}
                currentUserRole={currentUserRole}
                theme={theme}
              />
            )}
          </>
        )}
      </div>
    </LayoutTopSideBottom>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
  aideConsole: state.aideConsole,
});

export default connect(mapStateToProps)(AIDEConsole);
